import clsx from 'clsx'
import {KeyboardEvent, useEffect, useState} from 'react'
import {KTSVG} from '../../../helpers'
import {useThemeMode} from '../../../partials/layout/theme-mode/ThemeModeProvider'
import {useLayout} from '../../core'

type AsideVisibilityToggleProps = {
  forceHidden?: boolean
  mobileClassName?: string
  showMobileToggle?: boolean
}

const AsideVisibilityToggle = ({
  forceHidden = false,
  mobileClassName,
  showMobileToggle = true,
}: AsideVisibilityToggleProps) => {
  const {config} = useLayout()
  const {layoutWidth} = useThemeMode()
  const appTitle = process.env.REACT_APP_TITLE?.toLowerCase()
  const shouldShowToggle =
    !forceHidden && appTitle !== 'dsos' && config.aside?.display !== false && layoutWidth === 'full'
  const [isDesktopAsideHidden, setIsDesktopAsideHidden] = useState<boolean>(() => {
    if (typeof document === 'undefined' || layoutWidth !== 'full') {
      return false
    }

    return document.body.classList.contains('aside-hidden')
  })
  const [isMobileAsideOpen, setIsMobileAsideOpen] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const {body} = document
    if ((forceHidden || isDesktopAsideHidden) && layoutWidth === 'full') {
      body.classList.add('aside-hidden')
    } else {
      body.classList.remove('aside-hidden')
    }

    return () => body.classList.remove('aside-hidden')
  }, [forceHidden, isDesktopAsideHidden, layoutWidth])

  useEffect(() => {
    if (config.aside?.display === false || layoutWidth !== 'full' || !shouldShowToggle) {
      setIsDesktopAsideHidden(false)
    }
  }, [config.aside?.display, layoutWidth, shouldShowToggle])

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    const aside = document.getElementById('kt_aside')
    if (!aside) {
      return undefined
    }

    const syncMobileAsideState = () => {
      setIsMobileAsideOpen(aside.classList.contains('drawer-on'))
    }

    syncMobileAsideState()
    const observer = new MutationObserver(syncMobileAsideState)
    observer.observe(aside, {attributes: true, attributeFilter: ['class']})

    return () => observer.disconnect()
  }, [])

  const handleToggle = () => {
    if (typeof window !== 'undefined' && window.innerWidth >= 992 && layoutWidth === 'full') {
      setIsDesktopAsideHidden((current) => !current)
    }
  }

  const handleMobileKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      typeof window !== 'undefined' &&
      window.innerWidth < 992
    ) {
      event.preventDefault()
      event.currentTarget.click()
    }
  }

  if (!shouldShowToggle) {
    return null
  }

  return (
    <>
      {showMobileToggle && (
        <button
          type='button'
          className={clsx('btn btn-icon btn-active-icon-primary d-lg-none', mobileClassName, {
            active: isDesktopAsideHidden,
          })}
          id='kt_aside_toggle'
          aria-controls='kt_aside'
          aria-expanded={isMobileAsideOpen}
          aria-label={isMobileAsideOpen ? 'Global menüyü kapat' : 'Global menüyü aç'}
          onClick={handleToggle}
          onKeyDown={handleMobileKeyDown}
        >
          <KTSVG path='/media/icons/duotune/abstract/abs015.svg' className='svg-icon-1' />
        </button>
      )}
      <button
        type='button'
        className='aside-edge-toggle'
        aria-controls='kt_aside'
        aria-expanded={!isDesktopAsideHidden}
        aria-label={isDesktopAsideHidden ? 'Global menüyü aç' : 'Global menüyü kapat'}
        onClick={handleToggle}
      />
    </>
  )
}

export {AsideVisibilityToggle}
